<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useFinanceApi } from '../composables/useFinanceApi'
import { firstDayOfMonth, formatAmount, formatRate, toIsoDate } from '../utils/format'
import type {
  CurrencyOption,
  ReportGroupBy,
  RevenueVsCollectionsRow,
  RevenueVsCollectionsTotals,
} from '../types/finance'

const { t } = useI18n()
const { revenueVsCollections, listCurrencies } = useFinanceApi()

const today = toIsoDate()
const currencies = ref<CurrencyOption[]>([])
const rows = ref<RevenueVsCollectionsRow[]>([])
const totals = ref<RevenueVsCollectionsTotals | null>(null)
const loading = ref(true)
const error = ref('')

const filters = ref({
  date_from: firstDayOfMonth(),
  date_to: today,
  currency_code: '',
  group_by: 'month' as ReportGroupBy,
})

const groupOptions: ReportGroupBy[] = ['day', 'week', 'month']

const maxInvoiced = computed(() => {
  const values = rows.value.map((row) => Math.abs(Number(row.invoiced_amount ?? 0)))
  return values.length ? Math.max(...values) : 0
})

function barWidth(value: number): string {
  if (!maxInvoiced.value) return '0%'
  return `${Math.min(100, (Math.abs(value) / maxInvoiced.value) * 100)}%`
}

const validationError = computed(() =>
  filters.value.date_from > filters.value.date_to ? t('finance.common.invalidRange') : '',
)

async function load() {
  loading.value = true
  error.value = ''
  if (validationError.value) {
    error.value = validationError.value
    loading.value = false
    return
  }
  try {
    const result = await revenueVsCollections({
      date_from: filters.value.date_from,
      date_to: filters.value.date_to,
      currency_code: filters.value.currency_code,
      group_by: filters.value.group_by,
    })
    rows.value = result.rows
    totals.value = result.summary
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadCurrencies() {
  try {
    currencies.value = await listCurrencies()
  } catch {
    currencies.value = []
  }
}

onMounted(() => {
  void loadCurrencies()
  void load()
})
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('finance.nav.revenueVsCollections') }}</h2>
  </div>

  <div class="toolbar">
    <input v-model="filters.date_from" class="date-input" type="date">
    <input v-model="filters.date_to" class="date-input" type="date">
    <select v-model="filters.currency_code" class="filter-select">
      <option value="">{{ t('finance.common.allCurrencies') }}</option>
      <option v-for="currency in currencies" :key="currency.id" :value="currency.code">
        {{ currency.code }}
      </option>
    </select>
    <select v-model="filters.group_by" class="filter-select">
      <option v-for="option in groupOptions" :key="option" :value="option">
        {{ t(`finance.groupBy.${option}`) }}
      </option>
    </select>
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('finance.common.apply') }}
    </button>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <template v-else>
    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.reports.invoiced') }}</span>
        <span class="summary-value">{{ formatAmount(totals?.invoiced_total ?? 0) }}</span>
        <span class="summary-label" v-if="filters.currency_code">{{ filters.currency_code }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.reports.collected') }}</span>
        <span class="summary-value">{{ formatAmount(totals?.collected_total ?? 0) }}</span>
      </div>
      <div class="summary-card" :class="{ attention: (totals?.collection_gap ?? 0) > 0 }">
        <span class="summary-label">{{ t('finance.reports.gap') }}</span>
        <span class="summary-value">{{ formatAmount(totals?.collection_gap ?? 0) }}</span>
        <span class="summary-label">
          {{ t('finance.reports.rate') }}: {{ formatRate(totals?.collection_rate) }}
        </span>
      </div>
    </div>

    <section v-if="totals" class="panel" style="margin-bottom: 18px">
      <div class="panel-header">
        <h3>{{ t('finance.nav.collections') }}</h3>
      </div>
      <table class="data-table compact">
        <tbody>
          <tr>
            <td class="field-label">{{ t('finance.reports.allocatedCollections') }}</td>
            <td class="field-value cell-num">{{ formatAmount(totals.allocated_collections) }}</td>
            <td class="field-label">{{ t('finance.reports.unallocatedCollections') }}</td>
            <td class="field-value cell-num">{{ formatAmount(totals.unallocated_collections) }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('finance.reports.customerAdvances') }}</td>
            <td class="field-value cell-num">{{ formatAmount(totals.customer_advances) }}</td>
            <td class="field-label">{{ t('finance.reports.refunds') }}</td>
            <td class="field-value cell-num">{{ formatAmount(totals.refunds) }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('finance.reports.creditNotes') }}</td>
            <td class="field-value cell-num">{{ formatAmount(totals.credit_notes) }}</td>
            <td class="field-label">{{ t('finance.reports.debitNotes') }}</td>
            <td class="field-value cell-num">{{ formatAmount(totals.debit_notes) }}</td>
          </tr>
          <tr>
            <td class="field-label">{{ t('finance.reports.writeOffs') }}</td>
            <td class="field-value cell-num">{{ formatAmount(totals.write_offs) }}</td>
            <td class="field-label">{{ t('finance.reports.rate') }}</td>
            <td class="field-value cell-num">{{ formatRate(totals.collection_rate) }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <div v-if="rows.length === 0" class="state-message"><p>{{ t('finance.reports.empty') }}</p></div>

    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.reports.period') }}</th>
            <th class="cell-num">{{ t('finance.reports.invoiced') }}</th>
            <th class="cell-num">{{ t('finance.reports.collected') }}</th>
            <th class="cell-num">{{ t('finance.reports.gap') }}</th>
            <th class="cell-num">{{ t('finance.reports.rate') }}</th>
            <th>{{ t('finance.common.detail') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.period">
            <td class="cell-name">{{ row.period }}</td>
            <td class="cell-num">{{ formatAmount(row.invoiced_amount) }}</td>
            <td class="cell-num is-positive">{{ formatAmount(row.collected_amount) }}</td>
            <td class="cell-num" :class="Number(row.gap_amount) > 0 ? 'is-negative' : ''">
              {{ formatAmount(row.gap_amount) }}
            </td>
            <td class="cell-num">{{ formatRate(row.collection_rate) }}</td>
            <td>
              <div class="bar-track" :title="formatAmount(row.collected_amount)">
                <div class="bar-fill" :style="{ width: barWidth(row.collected_amount) }" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </template>
</template>
